import { LogOut, Monitor, Moon, Sun, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';

import { MenuToggleButton } from '@/components/navigation/MenuToggleButton';
import { Button } from '@/components/ui/Button';
import { logout } from '@/features/auth/store/authSlice';
import { PwaInstallButton } from '@/features/pwa/components/PwaInstallButton';
import { closeSideDrawer, openSideDrawer, toggleTheme } from '@/features/ui/store/uiSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { routePaths } from '@/routes/paths';
import { cn } from '@/utils/cn';

export function Header() {
  const dispatch = useAppDispatch();
  const theme = useAppSelector(state => state.ui.theme);
  const isDrawerOpen = useAppSelector(state => state.ui.isSideDrawerOpen);
  const userName = useAppSelector(state => state.auth.userName);
  const userEmail = useAppSelector(state => state.auth.userEmail);
  const themeLabel = theme === 'system' ? 'Auto' : theme === 'dark' ? 'Dark' : 'Light';
  const themeIcon =
    theme === 'system' ? (
      <Monitor className="h-4 w-4" />
    ) : theme === 'dark' ? (
      <Sun className="h-4 w-4" />
    ) : (
      <Moon className="h-4 w-4" />
    );

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200/55 bg-slate-50/78 shadow-[0_1px_0_rgba(148,163,184,0.06)] backdrop-blur-2xl dark:border-slate-800/70 dark:bg-slate-950/72">
      <div className="mx-auto flex min-h-[3.7rem] w-full max-w-[var(--app-content-max)] items-center justify-between gap-2.5 px-3 py-1.5 sm:px-4 md:px-6">
        <div className="flex min-w-0 items-center gap-2 sm:gap-2.5">
          <MenuToggleButton
            isOpen={isDrawerOpen}
            compact
            className="rounded-[1.15rem] border border-slate-200/75 bg-white/88 text-slate-600 shadow-[0_12px_28px_-24px_rgba(15,23,42,0.4)] hover:border-slate-300/80 hover:bg-white lg:hidden dark:border-slate-700/80 dark:bg-slate-900/88 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-slate-900"
            onClick={() => dispatch(isDrawerOpen ? closeSideDrawer() : openSideDrawer())}
          />
        </div>

        <div className="flex flex-1 items-center justify-end gap-1.5 sm:gap-2">
          <div className="hidden xl:block">
            <PwaInstallButton variant="secondary" showHelper={false} />
          </div>
          <Link
            to={routePaths.app.profile}
            className="hidden min-w-0 items-center gap-2.5 rounded-[1.1rem] border border-slate-200/70 bg-white/78 px-2.5 py-1.5 text-left shadow-[0_14px_30px_-28px_rgba(15,23,42,0.34)] transition hover:border-slate-300/80 hover:bg-white/92 md:flex dark:border-slate-700/80 dark:bg-slate-800/78 dark:shadow-[0_16px_34px_-30px_rgba(2,6,23,0.8)] dark:hover:border-slate-600 dark:hover:bg-slate-800/92"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-[0.95rem] bg-brand-50/80 text-brand-700 dark:bg-slate-700/90 dark:text-slate-200">
              <UserRound className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-slate-900 dark:text-white">{userName ?? 'Profile'}</span>
              <span className="block truncate text-[11px] font-medium text-slate-500/90 dark:text-slate-400">{userEmail ?? 'Open profile'}</span>
            </span>
          </Link>

          <button
            aria-label="Toggle theme"
            title={`Theme: ${themeLabel}`}
            className={cn(
              'tap-target inline-flex items-center gap-2 rounded-[1.1rem] border border-slate-200/75 bg-white/82 px-3 text-sm font-semibold text-slate-600 shadow-[0_12px_28px_-24px_rgba(15,23,42,0.35)] transition hover:border-slate-300/80 hover:bg-white dark:border-slate-700/80 dark:bg-slate-900/88 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-slate-900',
              'md:min-w-[5.5rem] md:justify-center'
            )}
            onClick={() => dispatch(toggleTheme())}
          >
            {themeIcon}
            <span className="hidden md:inline">{themeLabel}</span>
          </button>

          <Button
            className="rounded-[1.1rem] bg-slate-900 px-3 text-white shadow-[0_16px_30px_-24px_rgba(15,23,42,0.65)] hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            onClick={() => dispatch(logout())}
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
